// The small shared pieces of the dashboard: how a number is written, how a date
// is said, and the one stat block every card draws.
//
// They were private to `cards.tsx` until the screen grew a top row of KPIs, an
// upcoming-schedule column and a performance panel, all of which have to count
// and phrase things the same way. A second `Intl.NumberFormat` somewhere else
// on this page is how "1,240" and "1240" end up next to each other.

import type { ReactNode } from "react";
import type { DashboardDestination } from "./types";
import { formatInZone, instantToWallClock } from "../lib/timezone";

export const nf = new Intl.NumberFormat();

/** A share as a percentage, with no decimal noise and no division by zero. */
export function rate(part: number, whole: number): string {
  if (whole <= 0) return "—";
  const pct = (part / whole) * 100;
  return `${pct >= 99.5 || pct === 0 || pct % 1 === 0 ? pct.toFixed(0) : pct.toFixed(1)}%`;
}

export const dayLabel = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });

/** "in 6 days" / "tomorrow" / "today" — the shape a deadline is read in. */
export function countdown(iso: string): string {
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (days <= 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

// Every one of these takes the reader's zone rather than reaching for the
// browser's. A dashboard is the one screen where several people compare the
// same numbers, and "next: Fri 9:00" has to mean the same Friday to all of them.

export const when = (iso: string, zone: string) =>
  formatInZone(iso, zone, {
    weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
  });

/** Just the clock, for a list already grouped under a day. */
export const clock = (iso: string, zone: string) =>
  formatInZone(iso, zone, { hour: "numeric", minute: "2-digit" });

/** "Today" / "Tomorrow" / "Friday, 26 Sep" — the heading a schedule groups under. */
export function dayHeading(iso: string, zone: string): string {
  // Compared as calendar dates in the reader's zone, not as instants: "today"
  // is a date, and 23:00 tonight is one hour and one day away at the same time.
  const dayOf = (d: Date | string) => instantToWallClock(d, zone).slice(0, 10);
  const asUtc = (key: string) => Date.parse(`${key}T00:00:00Z`);
  const days = Math.round((asUtc(dayOf(iso)) - asUtc(dayOf(new Date()))) / 86_400_000);
  if (days === 0) return "Today";
  if (days === 1) return "Tomorrow";
  if (days === -1) return "Yesterday";
  return formatInZone(iso, zone, { weekday: "long", day: "numeric", month: "short" });
}

/** A headline number, what it is, and how it compares with the window before. */
export function Stat({
  label, value, previous, hint, tone,
}: {
  label: string;
  value: number;
  /** Omitted when there is nothing honest to compare against. */
  previous?: number;
  hint?: ReactNode;
  tone?: "ok" | "warn" | "err";
}) {
  // No percentage on a base of zero — "+∞%" is not a fact about anything.
  const delta = previous === undefined ? null : value - previous;
  const arrow = delta === null || delta === 0 ? null : delta > 0 ? "▲" : "▼";

  return (
    <div className="stat">
      <span className="stat-label muted">{label}</span>
      <strong className={tone ? `stat-value ${tone}` : "stat-value"}>{nf.format(value)}</strong>
      {delta !== null && (
        <span className="stat-delta muted">
          {arrow ? `${arrow} ${nf.format(Math.abs(delta))}` : "no change"}
          {" "}vs the period before
        </span>
      )}
      {hint && <span className="stat-hint muted">{hint}</span>}
    </div>
  );
}

/**
 * One account's standing, in the words someone would use about it.
 *
 * The same rule the server counts by, applied to a single row: a credential that
 * can refresh itself is never "expiring", because a Google token is meant to be
 * past its hour between posts. A live deadline outranks `status`, which is only
 * set when a publish *fails* — an account can read "active" while its token is
 * already dead, and that silent case is the one worth naming.
 */
export function health(x: DashboardDestination): { tone: "ok" | "warn" | "err"; label: string } {
  if (x.status === "expired") return { tone: "err", label: "Reconnect needed" };
  if (x.expires_at) {
    const ms = new Date(x.expires_at).getTime() - Date.now();
    if (ms <= 0) return { tone: "err", label: "Access expired" };
    if (ms < 14 * 86_400_000) return { tone: "warn", label: `Expires ${countdown(x.expires_at)}` };
  }
  return { tone: "ok", label: "Active" };
}
